import { useState } from "react";
import {
  Alert,
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CURVE_DEFINITIONS, formatEngineeringNumber } from "../../utils/windConfig";

function formatCurveTick(value) {
  return typeof value === "number"
    ? value.toLocaleString("en-GB", { maximumFractionDigits: 2 })
    : value;
}

function inferHeader(label, fallbackLabel, fallbackUnit) {
  if (!label) {
    return `${fallbackLabel}${fallbackUnit ? ` (${fallbackUnit})` : ""}`;
  }
  return label;
}

function SummaryRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(140px, 190px) minmax(72px, 1fr)",
        gap: 1,
      }}
    >
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

function formatSummaryValue(value) {
  if (value === 1 || value === true || value === "1" || value === "true") {
    return "Yes";
  }
  if (value === 0 || value === false || value === "0" || value === "false") {
    return "No";
  }
  if (value == null || value === "") return "-";
  return value;
}

function SummaryColumn({ items }) {
  return (
    <Stack spacing={1.15}>
      {items.map((item) => (
        <SummaryRow key={item.label} label={item.label} value={item.value} />
      ))}
    </Stack>
  );
}

function CurveDisplayBlock({ curve, viewMode }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const chartAxis = theme.palette.text.secondary;
  const chartGrid = isDark ? theme.palette.divider : "#d9e2f1";
  const chartLine = isDark ? theme.palette.primary.main : "#2563eb";
  const chartTooltipStyle = isDark
    ? {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        color: theme.palette.text.primary,
      }
    : undefined;
  const fallbackDefinition = CURVE_DEFINITIONS[curve?.key] || {};
  const points = Array.isArray(curve?.points) ? curve.points : [];
  const xHeader = inferHeader(
    curve?.x_label,
    fallbackDefinition.xLabel || "X",
    fallbackDefinition.xUnit || ""
  );
  const yHeader = inferHeader(
    curve?.y_label,
    fallbackDefinition.yLabel || "Y",
    fallbackDefinition.yUnit || ""
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, height: "100%" }}>
      <Stack spacing={1.5} sx={{ height: "100%" }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {curve?.label || fallbackDefinition.label || curve?.key || "Curve"}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {curve?.point_count ?? points.length} points
          </Typography>
        </Box>

        {viewMode === "graph" ? (
          <Box sx={{ width: "100%", height: 420 }}>
            <ResponsiveContainer>
              <LineChart data={points} margin={{ top: 18, right: 28, left: 18, bottom: 18 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} />
                <XAxis
                  dataKey="x"
                  tickFormatter={formatCurveTick}
                  tick={{ fill: chartAxis, fontSize: 12 }}
                  stroke={chartAxis}
                  label={{ value: xHeader, position: "insideBottom", offset: -6, fontSize: 12, fill: chartAxis }}
                />
                <YAxis
                  width={104}
                  tickFormatter={formatCurveTick}
                  tick={{ fill: chartAxis, fontSize: 12 }}
                  stroke={chartAxis}
                  label={{
                    value: yHeader,
                    angle: -90,
                    position: "insideLeft",
                    fontSize: 12,
                    fill: chartAxis,
                  }}
                />
                <RechartsTooltip
                  formatter={(value) => [formatCurveTick(value), yHeader]}
                  labelFormatter={(label) => `${xHeader}: ${formatCurveTick(label)}`}
                  contentStyle={chartTooltipStyle}
                  labelStyle={isDark ? { color: theme.palette.text.secondary } : undefined}
                  itemStyle={isDark ? { color: theme.palette.text.primary } : undefined}
                />
                <Line
                  type="monotone"
                  dataKey="y"
                  stroke={chartLine}
                  strokeWidth={2.75}
                  dot={{ r: 2.5, fill: chartLine, stroke: chartLine }}
                  activeDot={{ r: 4, fill: chartLine, stroke: chartLine }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <TableContainer variant="outlined" sx={{ borderRadius: 2, maxHeight: 420 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>{xHeader}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{yHeader}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {points.map((point, index) => (
                  <TableRow key={`${curve?.key || "curve"}-${index}`}>
                    <TableCell>{formatEngineeringNumber(point.x, 4)}</TableCell>
                    <TableCell>{formatEngineeringNumber(point.y, 4)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>
    </Paper>
  );
}

export default function TurbineDetailsCard({
  details,
  loading = false,
  error = "",
  emptyMessage = "Select a turbine to review its details.",
}) {
  const curves = Array.isArray(details?.curves) ? details.curves : [];
  const [viewMode, setViewMode] = useState("graph");
  const characteristics = details?.characteristics || {};

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Typography variant="body2">Loading turbine details...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!details) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {emptyMessage}
        </Typography>
      </Paper>
    );
  }

  const summaryItems = [
    {
      label: "Manufacturer",
      value: details.manufacturer || "-",
    },
    {
      label: "Turbine type",
      value: formatSummaryValue(characteristics.turbine_type || details.name),
    },
    {
      label: "Nominal power (kW)",
      value:
        details.nominal_power_kw != null
          ? formatEngineeringNumber(details.nominal_power_kw, 2)
          : "-",
    },
    {
      label: "Rotor diameter (m)",
      value:
        details.rotor_diameter_m != null
          ? formatEngineeringNumber(details.rotor_diameter_m, 2)
          : "-",
    },
    {
      label: "Has power curve",
      value: formatSummaryValue(characteristics.has_power_curve),
    },
    {
      label: "Has power coefficient curve",
      value: formatSummaryValue(characteristics.has_cp_curve),
    },
    {
      label: "Source",
      value: formatSummaryValue(characteristics.source),
    },
  ];
  const summaryLeftColumn = summaryItems.slice(0, 4);
  const summaryRightColumn = summaryItems.slice(4);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, width: "100%" }}>
      <Stack spacing={2.5} sx={{ width: "100%" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {details.name || "Selected turbine"}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Summary fields and curves below are taken from the explicit turbine details
            response.
          </Typography>
        </Box>

        <Paper
          variant="outlined"
          sx={{
            p: 1.6,
            borderRadius: 2.25,
            width: "fit-content",
            minWidth: { xs: "100%", md: 760 },
            maxWidth: "100%",
            alignSelf: "flex-start",
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Summary
            </Typography>
            <Box
              sx={{
                display: "grid",
                gap: 1.25,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              <SummaryColumn items={summaryLeftColumn} />
              <SummaryColumn items={summaryRightColumn} />
            </Box>
          </Stack>
        </Paper>

        {curves.length > 0 ? (
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="flex-start"
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Performance curves
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={viewMode}
                onChange={(_event, nextMode) => {
                  if (nextMode) setViewMode(nextMode);
                }}
              >
                <ToggleButton value="graph">Graph</ToggleButton>
                <ToggleButton value="table">Table</ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gap: 2,
                width: "100%",
                gridTemplateColumns:
                  curves.length > 1
                    ? {
                        xs: "minmax(0, 1fr)",
                        xl: "repeat(2, minmax(0, 1fr))",
                      }
                    : "minmax(0, 1fr)",
              }}
            >
              {curves.map((curve, index) => (
                <CurveDisplayBlock
                  key={curve.key || curve.label || index}
                  curve={curve}
                  viewMode={viewMode}
                />
              ))}
            </Box>
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            No chartable curves were returned.
          </Typography>
        )}

      </Stack>
    </Paper>
  );
}
